<template>
    <div class="flex-grow-1 d-flex flex-column">
        <div class="d-flex justify-content-between align-items-baseline">
            <span class="text-dark">
              {{ count }} Results
              <span class="font-weight-bold"
                    v-if="selectedCount > 0"
              >
                | {{ selectedCount }} selected
              </span>
            </span>
            <div class="d-flex justify-content-end mb-4">
                <reset-filters
                        @click="resetGridToDefaultOptions"></reset-filters>
                <refresh-button
                        @click="refresh"
                        :spinning="refreshing"
                >
                </refresh-button>
                <b-dropdown
                    no-caret
                    right
                    variant="grey"
                    toggle-class="py-1 px-2"
                    class="ml-3"
                >
                    <template #button-content>
                        Actions
                        <font-awesome-icon
                                class="ml-1"
                                :icon="['fal', 'chevron-down']"
                                size="xs"
                        >
                        </font-awesome-icon>
                    </template>
                    <b-dropdown-item
                        @click="openModal('upload')"
                        v-if="$can($perm.PROPERTIES_UPLOAD_DOCUMENTS, $perm.PERMISSION_TYPE_PROPERTIES)"
                    >
                        <font-awesome-icon
                                :icon="['fal', 'upload']"
                        >
                        </font-awesome-icon>
                        Upload
                    </b-dropdown-item>
                    <b-dropdown-item
                        @click="openModal('delete')"
                        v-if="$can($perm.PROPERTIES_EDIT_PROPERTIES, $perm.PERMISSION_TYPE_PROPERTIES)"
                    >
                        <font-awesome-icon
                                :icon="['far', 'times']"
                        >
                        </font-awesome-icon>
                        Delete Selected
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <div class="flex-grow-1 d-flex">
            <ag-grid-vue
                    class="ag-theme-balham"
                    :style="'width: ' + gridWidth +'%;'"
                    :cacheQuickFilter="cacheQuickFilter"
                    @cellClicked="onCellClicked"

                    :columnDefs="columnDefs"
                    :defaultColDef="defaultColDef"
                    :floatingFilter="floatingFilter"
                    :side-bar="sideBarDef"

                    :gridOptions="gridOptions"

                    @gridReady="onGridReady"
                    :headerHeight="rowHeight"
                    :rowData="rowData"
                    :rowHeight="rowHeight"
                    :rowSelection="rowSelection"
                    :context="context"

                    @rowSelected="updateRowSelected"

                    :suppressContextMenu="true"

                    :suppressExcelExport="suppressExcelExport"
            >
            </ag-grid-vue>
        </div>

        <property-file-modal
                v-if="modals.upload.visible"
                :file-url="fileUrl"
                :show.sync="modals.upload.visible"
                @file-sent="onFilesUploaded"
                @hidden="modals.upload.visible = false"
        >
        </property-file-modal>
        <file-type-modal
                v-if="modals.fileType.visible"
                :show.sync="modals.fileType.visible"
                :property-id="this.propertyIdValue"
                :property-document-id="this.propertyDocumentId"
                @file-type-updated="fileTypeUpdated"
                @hidden="modals.fileType.visible = false"
        >
        </file-type-modal>
        <b-modal
                @ok="onDeleteFiles"
                cancel-variant="outline-secondary"
                id="deleteDocuments"
                ok-variant="danger"
                title="Delete files"
                v-model="modals.delete.visible"
        >
            <vue-element-loading :active="modals.delete.loading" :is-full-screen="false"/>
            Are you sure you want to delete these files?
            <template slot="modal-ok">
                Delete
            </template>
        </b-modal>

    </div>
</template>

<script>
import {
  gridDefaultColDef,
  gridColumns,
} from '@/services/Property/PropertyDocumentGrid';
import GridMixin from '@/components/Grid/GridMixin';
import PropertyDocument from '@/services/Property/PropertyDocument';
import PropertyFileModal from '@/components/Property/Modal/PropertyFileModal';
import api from '@/api';
import RefreshButton from '@/components/Grid/RefreshButton';
import FileTypeModal from '@/components/Modal/FileTypeModal';
import ResetFilters from '@/components/Grid/ResetFilters';

export default {
  name: 'Property-Document-Grid',
  components: {
    PropertyFileModal,
    RefreshButton,
    FileTypeModal,
    ResetFilters
  },
  mixins: [GridMixin],
  props: {
    property: {
      required: true,
      type: Object,
    },
    shown: {
      type: Boolean,
    },
  },
  data() {
    return {
      modelId: 'propertyFileId',
      rowData: [],
      context: null,
      defaultColDef: gridDefaultColDef,
      columnDefs: gridColumns,
      propertyDocumentId: 0,
      propertyIdValue: 0,
      modals: {
        delete: {
          loading: false,
          visible: false,
        },
        upload: {
          visible: false,
        },
        fileType: {
          visible: false,
        },
      },
    };
  },
  computed: {
    fileUrl() {
      return `${api.defaults.baseURL}/properties/${this.property.propertyId}/files`;
    },
  },
  beforeMount() {
    this.context = { componentParent: this };
  },
  methods: {
    postGridReady() {
      this.refresh();
    },

    async refresh() {
      this.refreshing = true;
      try {
        const { data } = await PropertyDocument.list(this.property.propertyId);
        this.rowData = data.files;
      } catch (e) {} finally {
        this.refreshing = false;
      }

        this.postRefresh();
    },

    /**
     * Call after every refresh
     * */
    postRefresh() {
      this.updateRowSelected();
    },

    /**
       * Opens a modal by name (in data -> modals.[name])
       */
    openModal(name) {
      if (this.refreshing) {
        this.$notify({
          title: 'Please wait..',
          text: 'Files are currently being loaded, please try again',
          type: 'warn',
        });

        return;
      }

      if (this.selectedIds.length === 0 && name !== 'upload') {
        this.$notify({
          title: 'No files selected',
          text: 'Please select at least one file.',
          type: 'warn',
        });

        return;
      }

      this.modals[name].visible = true;
    },

    /**
      * Opens the file type modal
      *
      * @param propertyDocumentId
      * @param propertyId
      */
    openFileTypeModal(propertyDocumentId, propertyId) {
      if (this.refreshing) {
        this.$notify({
          title: 'Please wait..',
          text: 'Files are currently being loaded, please try again',
          type: 'warn',
        });

        return;
      }

      if (!propertyDocumentId) {
        this.$notify({
          title: 'No files selected',
          text: 'Please select at least one file.',
          type: 'warn',
        });

        return;
      }

      this.modals.fileType.visible = true;
      this.propertyDocumentId = propertyDocumentId;
      this.propertyIdValue = propertyId;
    },
    async onDeleteFiles(bvEvt) {
      bvEvt.preventDefault();
      this.modals.delete.loading = true;
      try {
        await PropertyDocument.delete(this.property.propertyId, this.selectedIds);

        this.$notify({
          title: 'Success',
          text: 'Document(s) have been deleted.',
          type: 'success',
        });
        this.refresh();
      } catch (e) {

      } finally {
        this.modals.delete.visible = false;
        this.modals.delete.loading = false;
      }
    },

    onFilesUploaded() {
      this.$notify({
        title: 'Success',
        text: 'Document(s) have been uploaded.',
        type: 'success',
      });
      this.refresh();
    },

    fileTypeUpdated() {
      this.modals.fileType.visible = false;
      this.refresh();
    },
  },
};
</script>
